import {Component, effect, inject, model, ModelSignal, untracked} from '@angular/core';
import {
  ColTypeColDefMap,
  CSV_IMPORT_ACTION,
  DEFAULT_TABLE_ACTIONS,
  DisplayedColumns,
  ITableAction,
  TableApiAdapter,
  TableControlComponent,
  TableControlModule,
} from 'frontier/browserkit';
import AppControlGuids from '../../../api/ControlGuids';
import {InvoiceAutomationTabService} from './invoice-automation-tab.service';
import {IInvoiceTemplate} from './template.interface';

// Captures the backend-assigned attributeindex of the InvoiceTemplate column from the first API
// response so it can be used in column filters later. The index is not known until the API responds.
export class InvoiceAutomationApiAdapter extends TableApiAdapter {
  invoiceTemplateAttributeIndex: number | undefined;

  constructor() {
    super({}, ColTypeColDefMap);
  }

  override from(apiData: any): any {
    (apiData.displayedColumns as DisplayedColumns[])?.forEach((col: DisplayedColumns): void => {
      if (col.attribute === 'InvoiceTemplate') {
        this.invoiceTemplateAttributeIndex = col.attributeindex;
      }
    });
    return super.from(apiData);
  }
}

@Component({
  selector: 'app-invoice-automation-editor',
  standalone: true,
  imports: [TableControlModule],
  templateUrl: '../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.html',
  styleUrl: '../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.scss',
})
export class InvoiceAutomationEditorComponent extends TableControlComponent {
  override GUID: string = AppControlGuids.TInvoiceAutomationView;
  override actions: ModelSignal<ITableAction[]> = model<ITableAction[]>([
    ...DEFAULT_TABLE_ACTIONS,
    CSV_IMPORT_ACTION,
  ]);
  override apiAdapter: InvoiceAutomationApiAdapter = new InvoiceAutomationApiAdapter();

  private _tabService: InvoiceAutomationTabService = inject(InvoiceAutomationTabService);
  // Holds the template to filter by until the first data load completes.
  // undefined = no pending filter; null = open without filter; object = filter by template name.
  private _pendingTemplate: IInvoiceTemplate | null | undefined;

  constructor() {
    super();
    // Claim the pending template immediately so a second component instance doesn't pick it up.
    this._pendingTemplate = this._tabService.pendingTemplate;
    this._tabService.pendingTemplate = undefined;

    // The attributeindex is only available after apiAdapter.from() runs (on the first data load).
    // loading() transitions true → false after from() completes, so we wait for that moment.
    effect((): void => {
      const isLoading: boolean = this.loading();
      if (isLoading) return;
      if (this._pendingTemplate === undefined) return;
      if (this.apiAdapter.invoiceTemplateAttributeIndex === undefined) return;

      const template: IInvoiceTemplate | null = this._pendingTemplate;
      this._pendingTemplate = undefined; // consume once

      if (template) {
        // untracked prevents patchFilter from being tracked as a dependency of this effect,
        // which would cause an infinite loop.
        untracked((): void => {
          this.patchFilter({
            columnfilter: [{
              attribute: 'InvoiceTemplate',
              attributeindex: this.apiAdapter.invoiceTemplateAttributeIndex,
              expression: {filterType: 'text', type: 'equals', filter: template.name},
            }],
          });
        });
      }
    });
  }
}
